import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  enqueueDocumentPreview,
  fetchDocumentPreviewInfo,
} from "../../../services/documentPreviewService";
import {
  buildDocumentDownloadUrl,
  buildFileTitle,
  createInlineObjectUrl,
  downloadOriginalDocument,
  inferFileExtension,
} from "./documentUrlUtils";
import PdfJsPreviewViewer from "./PdfJsPreviewViewer";
import "./DocumentPreview.scss";

const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "webp"]);
const OFFICE_EXTS = new Set([
  "doc",
  "docx",
  "dot",
  "dotx",
  "xls",
  "xlsx",
  "xlsm",
  "csv",
  "ppt",
  "pptx",
  "odt",
  "ods",
  "odp",
  "rtf",
  "txt",
]);

const IMAGE_MIME = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
};

/**
 * Platform-wide document preview (PDF via PDF.js / image).
 * Use across QMS, eISF, Volunteer, Staff, Schedule, etc.
 */
export default function DocumentPreviewPane({
  active = true,
  sourceModule,
  sourceTable = "",
  documentId,
  documentUrl,
  documentExtension,
  documentTitle = "Document",
  pollMs = 4000,
  className = "",
  autoEnqueueIfMissing = true,
  showDownloadOriginal = true,
}) {
  const extension = useMemo(
    () => inferFileExtension(documentExtension, documentUrl, documentTitle),
    [documentExtension, documentUrl, documentTitle],
  );
  const fileTitle = useMemo(
    () => buildFileTitle(documentTitle, extension),
    [documentTitle, extension],
  );
  const originalUrl = useMemo(
    () => buildDocumentDownloadUrl(documentUrl),
    [documentUrl],
  );
  const isImage = IMAGE_EXTS.has(extension);
  const isPdf = extension === "pdf";
  const needsConversion = OFFICE_EXTS.has(extension);

  const [previewUrl, setPreviewUrl] = useState("");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const enqueueAttemptedRef = useRef(false);
  const objectUrlRef = useRef("");

  const revokeObjectUrl = useCallback(() => {
    if (!objectUrlRef.current) return;
    URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = "";
  }, []);

  const setInlinePreviewUrl = useCallback(
    (nextUrl) => {
      revokeObjectUrl();
      if (nextUrl && String(nextUrl).startsWith("blob:")) {
        objectUrlRef.current = nextUrl;
      }
      setPreviewUrl(nextUrl || "");
    },
    [revokeObjectUrl],
  );

  const handleDownloadOriginal = useCallback(() => {
    void downloadOriginalDocument({
      documentUrl,
      fileName: fileTitle,
    });
  }, [documentUrl, fileTitle]);

  useEffect(() => {
    enqueueAttemptedRef.current = false;
  }, [documentId, documentUrl, sourceModule]);

  useEffect(() => () => revokeObjectUrl(), [revokeObjectUrl]);

  useEffect(() => {
    if (!active) return undefined;

    if (!originalUrl) {
      setInlinePreviewUrl("");
      setStatus("Missing");
      setMessage("Document URL is missing.");
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    let timerId = null;

    const run = async () => {
      // PDF / image: via API Inline proxy (Azure attachment → previewable stream).
      if (isImage || isPdf) {
        setStatus("NotRequired");
        setMessage("");
        setLoading(true);
        // Clear previous doc so PDF.js does not briefly paint the old file.
        setInlinePreviewUrl("");
        try {
          const mime = isPdf
            ? "application/pdf"
            : IMAGE_MIME[extension] || "application/octet-stream";
          const inlineUrl = await createInlineObjectUrl(
            originalUrl,
            mime,
            fileTitle,
          );
          if (cancelled) {
            if (inlineUrl && String(inlineUrl).startsWith("blob:")) {
              URL.revokeObjectURL(inlineUrl);
            }
            return;
          }
          setInlinePreviewUrl(inlineUrl);
        } catch {
          if (cancelled) return;
          setInlinePreviewUrl("");
          setStatus("Error");
          setMessage(
            "Preview could not be loaded. Use Download original, or try again.",
          );
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      if (!needsConversion || !sourceModule || !documentId) {
        setInlinePreviewUrl("");
        setStatus("Unavailable");
        setMessage(
          "Preview is not available for this file type yet. Download the original file.",
        );
        setLoading(false);
        return;
      }

      const loadInfo = async () => {
        setLoading(true);
        try {
          const { info, message: apiMessage } = await fetchDocumentPreviewInfo({
            sourceModule,
            documentId,
          });

          if (cancelled) return;

          const mode = String(
            info?.PreviewMode || info?.previewMode || "",
          ).toLowerCase();
          const canPreview = Boolean(info?.CanPreview ?? info?.canPreview);
          const nextStatus = String(info?.Status || info?.status || "Missing");
          const nextUrl = String(
            info?.PreviewUrl || info?.previewUrl || "",
          ).trim();

          setStatus(nextStatus);
          setMessage(apiMessage || info?.Message || info?.message || "");

          if (canPreview && nextUrl && (mode === "pdf" || mode === "image")) {
            const remote = buildDocumentDownloadUrl(nextUrl) || nextUrl;
            try {
              const mime =
                mode === "image"
                  ? IMAGE_MIME[extension] || "image/png"
                  : "application/pdf";
              const inlineUrl = await createInlineObjectUrl(
                remote,
                mime,
                fileTitle,
              );
              if (cancelled) {
                if (inlineUrl && String(inlineUrl).startsWith("blob:")) {
                  URL.revokeObjectURL(inlineUrl);
                }
                return;
              }
              setInlinePreviewUrl(inlineUrl);
            } catch {
              if (cancelled) return;
              setInlinePreviewUrl("");
              setStatus("Error");
              setMessage(
                "Preview could not be loaded. Use Download original, or try again.",
              );
            }
            setLoading(false);
            return;
          }

          if (
            autoEnqueueIfMissing &&
            !enqueueAttemptedRef.current &&
            (nextStatus === "Missing" || !info)
          ) {
            enqueueAttemptedRef.current = true;
            await enqueueDocumentPreview({
              sourceModule,
              sourceTable: sourceTable || "",
              documentId,
              blobUrl: originalUrl,
              fileExtension: extension,
              priority: 50,
            });
          }

          setInlinePreviewUrl("");
          const pending =
            /pending|processing|retry|missing/i.test(nextStatus) || !info;
          setLoading(pending);

          if (pending) {
            timerId = window.setTimeout(loadInfo, pollMs);
          }
        } catch (error) {
          if (cancelled) return;
          setInlinePreviewUrl("");
          setStatus("Error");
          setMessage(error?.message || "Unable to load preview info.");
          setLoading(false);
          timerId = window.setTimeout(loadInfo, Math.max(pollMs, 8000));
        }
      };

      await loadInfo();
    };

    void run();

    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [
    active,
    autoEnqueueIfMissing,
    documentId,
    extension,
    fileTitle,
    isImage,
    isPdf,
    needsConversion,
    originalUrl,
    pollMs,
    setInlinePreviewUrl,
    sourceModule,
    sourceTable,
  ]);

  const viewer = (() => {
    if (!originalUrl) {
      return (
        <div className="aq-doc-preview__empty">Document URL is missing.</div>
      );
    }

    if (loading && !previewUrl) {
      return (
        <div className="aq-doc-preview__empty aq-doc-preview__empty--loading">
          {isPdf || isImage
            ? "Loading preview…"
            : "Generating PDF preview…"}
        </div>
      );
    }

    if (isImage && previewUrl) {
      return (
        <div className="aq-doc-preview__image-wrap">
          <img
            src={previewUrl}
            alt={fileTitle}
            className="aq-doc-preview__image"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </div>
      );
    }

    if (previewUrl) {
      return (
        <PdfJsPreviewViewer fileUrl={previewUrl} fileTitle={fileTitle} />
      );
    }

    return (
      <div className="aq-doc-preview__empty">
        <p className="mb-1">
          {message ||
            "Preview unavailable. Download the original document."}
        </p>
        {status ? (
          <p className="text-muted small mb-0">Status: {status}</p>
        ) : null}
      </div>
    );
  })();

  return (
    <div
      className={`aq-doc-preview ${className}`.trim()}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {showDownloadOriginal && originalUrl ? (
        <div className="qms-doc-preview-modal__toolbar aq-doc-preview__toolbar">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary qms-doc-preview-modal__download-btn"
            onClick={handleDownloadOriginal}
            title="Download the original uploaded file"
          >
            <i className="fas fa-download me-1" aria-hidden="true" />
            Download original
          </button>
          <span className="qms-doc-preview-modal__toolbar-hint">
            Downloads the original file{extension ? ` (.${extension})` : ""}.
            Office files preview as PDF.
          </span>
        </div>
      ) : null}
      <div className="aq-doc-preview__viewer" style={{ flex: 1, minHeight: 0 }}>
        {viewer}
      </div>
    </div>
  );
}
